import React, { useState } from "react";

/**
 * Parses a WhatsApp order message and matches products against the POS menu.
 *
 * Line format: `2x Hamburguesa Clásica (#142) - $180.00`
 * With size:   `1x Pizza Grande (#87-S23) - $220.00`
 */
const LINE_REGEX = /(\d+)\s*x\s+.+?\(#(\d+)(?:-S(\d+))?\)/;

function parseMessage(text, menuProducts) {
  const lines = text.split("\n").filter((l) => l.trim());
  const matched = [];
  const unmatched = [];

  // Build lookup by product ID
  const productMap = {};
  menuProducts.forEach((p) => {
    productMap[p.id] = p;
  });

  lines.forEach((line) => {
    const m = line.match(LINE_REGEX);
    if (!m) {
      // Skip header/footer lines (e.g. "Hola!", "Total: $X")
      if (/\d+\s*x\s+/.test(line)) {
        unmatched.push(line);
      }
      return;
    }

    const qty = parseInt(m[1], 10);
    const productId = m[2];
    const sizeId = m[3] || null;
    const product = productMap[productId];

    if (!product) {
      unmatched.push(line);
      return;
    }

    let price = product.price;
    let size = null;
    let needsSizeResolution = false;

    if (sizeId && product.sizes) {
      const sizesArr = Array.isArray(product.sizes)
        ? product.sizes
        : Object.values(product.sizes);
      size = sizesArr.find((s) => String(s.id) === String(sizeId));
      if (size) {
        price = size.price;
      }
    } else if (
      product.has_size &&
      product.sizes &&
      (Array.isArray(product.sizes)
        ? product.sizes.length > 0
        : Object.keys(product.sizes).length > 0)
    ) {
      needsSizeResolution = true;
    }

    // Merge duplicate product+size combos
    const key = `${productId}-${sizeId || "base"}`;
    const existing = matched.find((item) => item.key === key);
    if (existing) {
      existing.qty += qty;
    } else {
      matched.push({
        key,
        productId,
        product,
        qty,
        price,
        size,
        needsSizeResolution,
        title: product.title,
      });
    }
  });

  return { matched, unmatched };
}

const WpImportModal = ({ open, toggle, menuProducts, onImport }) => {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);

  const handleParse = () => {
    if (!text.trim()) return;
    const result = parseMessage(text, menuProducts);
    setParsed(result);
  };

  const handleImport = () => {
    if (!parsed || parsed.matched.length === 0) return;
    onImport(parsed.matched);
    setText("");
    setParsed(null);
    toggle();
  };

  const handleClose = () => {
    setText("");
    setParsed(null);
    toggle();
  };

  if (!open) return null;

  return (
    <div
      className="modal d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={handleClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fab fa-whatsapp text-success mr-2"></i>
              Importar Pedido de WhatsApp
            </h5>
            <button
              type="button"
              className="close"
              onClick={handleClose}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {!parsed ? (
              <>
                <p className="text-muted mb-2">
                  Pega el mensaje de WhatsApp del cliente con su pedido.
                </p>
                <textarea
                  className="form-control"
                  rows={8}
                  placeholder={`Ejemplo:\n2x Hamburguesa Clásica (#142) - $180.00\n1x Pizza Grande (#87-S23) - $220.00`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  autoFocus
                />
              </>
            ) : (
              <>
                {parsed.matched.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-success">
                      <i className="fas fa-check-circle mr-1"></i>
                      Productos encontrados ({parsed.matched.length})
                    </h6>
                    <table className="table table-sm table-bordered mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>Cant.</th>
                          <th>Producto</th>
                          <th>Tamaño</th>
                          <th className="text-right">Precio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsed.matched.map((item) => (
                          <tr key={item.key}>
                            <td>{item.qty}</td>
                            <td>
                              {item.title}
                              {item.needsSizeResolution && (
                                <span className="badge badge-warning ml-1">
                                  Sin tamaño
                                </span>
                              )}
                            </td>
                            <td>{item.size ? item.size.name : "—"}</td>
                            <td className="text-right">
                              ${(item.price * item.qty).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="font-weight-bold">
                          <td colSpan="3" className="text-right">
                            Total:
                          </td>
                          <td className="text-right">
                            $
                            {parsed.matched
                              .reduce(
                                (sum, item) => sum + item.price * item.qty,
                                0
                              )
                              .toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                {parsed.unmatched.length > 0 && (
                  <div>
                    <h6 className="text-danger">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      No encontrados ({parsed.unmatched.length})
                    </h6>
                    {parsed.unmatched.map((line, i) => (
                      <p
                        key={i}
                        className="mb-1 small text-danger"
                        style={{ fontFamily: "monospace" }}
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                )}
                {parsed.matched.length === 0 && parsed.unmatched.length === 0 && (
                  <p className="text-muted">
                    No se encontraron productos en el mensaje. Asegúrate de que
                    el mensaje fue generado desde el menú de Foo.
                  </p>
                )}
              </>
            )}
          </div>
          <div className="modal-footer">
            {!parsed ? (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={handleClose}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleParse}
                  disabled={!text.trim()}
                >
                  <i className="fas fa-search mr-1"></i>
                  Importar
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => setParsed(null)}
                >
                  <i className="fas fa-arrow-left mr-1"></i>
                  Volver
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleImport}
                  disabled={parsed.matched.length === 0}
                >
                  <i className="fas fa-cart-plus mr-1"></i>
                  Agregar al pedido ({parsed.matched.length})
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WpImportModal;
